/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

import { Procedure, useUpdateProcedureMutation } from '@/generated/graphql';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { showPopup } from '@/stores/slices/appPopup';
import { departmentQuerySelector } from '@/stores/slices/department';
import routes from '@/utils/routes';
import { isEmpty } from 'lodash';
import { message, Tooltip, Dropdown, MenuProps } from 'antd';
import { Button } from '@/uiKit';
import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useKogEditorContext } from '@/pages/PlaygroundV3/components/Editor/EditorProvider';
import AppConstants from '@/utils/AppConstants';
import { IProcedureOutletContext } from '@/pages/RunViewLayout';
import PlaygroundScriptIcon from '../icons/PlaygroundScriptIcon';

interface INewProcedureFromRunProps {
  workerId: string;
  procedure?: Procedure | null;
  editorContent?: string;
  isRunPage?: boolean;
}

// TODO: Remove once ALC is enabled across the app
function NewProcedureFromRun(props: INewProcedureFromRunProps) {
  const { workerId: _workerId, procedure, editorContent, isRunPage } = props;
  const outletContext = useOutletContext<IProcedureOutletContext>();

  const editorContext = useKogEditorContext();
  const { department } = useAppSelector(departmentQuerySelector);

  const [updateProcedure] = useUpdateProcedureMutation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onPlaygroundProcessCreate = () => {
    if (editorContent) {
      dispatch(
        showPopup({
          popupId: AppConstants.POPUPS.NEW_FLOW,
          popupParams: {
            onSuccess: (result: any) => {
              const p = result.createProcedure;
              message.success('New process created');
              navigate(
                routes.procedure.getProcedurePageUrl(p.departmentId, p.id)
              );
            },
            department,
            procedureBody: editorContent,
            suffix: ''
          }
        })
      );
    }
  };

  const updateCurrentProcess = () => {
    if (editorContent) {
      if (procedure?.id) {
        updateProcedure({
          variables: {
            input: {
              id: procedure?.id,
              departmentId: department.id,
              text: editorContent,
              name: procedure?.name,
              language: procedure?.language
            }
          },
          onCompleted: () => {
            message.success('Process updated');
            outletContext?.refetchProcedure();
          },
          onError: (e) => {
            console.log('update procedure error', e);
            message.error('Failed to update process. Please try');
          }
        });
      }
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'save-edits-to-current-process',
      label: (
        <span data-cy="save-edits-to-current-process">
          Save edits to current process
        </span>
      ),
      onClick: updateCurrentProcess
    },
    {
      key: 'create-new-process-from-run',
      label: (
        <span data-cy="create-new-process-from-run">
          Create new process from run
        </span>
      ),
      onClick: onPlaygroundProcessCreate
    }
  ];

  const isEditorActionsDisabled = editorContext?.editorActionsDisabled;

  if (isRunPage) {
    return (
      <Tooltip title="Save Process">
        <Dropdown menu={{ items: menuItems }} placement="bottomRight" arrow>
          <Button
            type="text"
            disabled={isEmpty(editorContent) || isEditorActionsDisabled}
            data-cy="save-process-button"
            className="display-flex align-center justify-center"
            icon={<PlaygroundScriptIcon />}
          />
        </Dropdown>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isRunPage ? 'Create Process from Run' : 'Save as process'}>
      <Button
        type="text"
        disabled={isEmpty(editorContent) || isEditorActionsDisabled}
        data-cy="new-procedure-button"
        className="display-flex align-center justify-center"
        onClick={onPlaygroundProcessCreate}
        icon={<PlaygroundScriptIcon />}
      />
    </Tooltip>
  );
}

export default NewProcedureFromRun;
